import moment from "moment"
import React from "react"
import useDidMountEffect from "../../hooks/useDidMountEffect"
import Pathicon from "../../pathicon/Pathicon"
import Tooltip from "../../tooltip/Tooltip"
import TimePicker from "../form-time-picker/TimePicker"
import DayPicker, { isDayDisabled } from "./DayPicker"

const hours = new Array(24).fill(0).map((h, i) => i)
const minutes = new Array(60).fill(0).map((m, i) => i)

export default function DayTimePicker({
  label,
  value,
  defaultHour,
  disabledDays,
  onChange,
  initialMonth,
  ...rest
}) {
  const [day, setDay] = React.useState(value /* Date */)

  // send value downstream
  useDidMountEffect(() => {
    if (day !== value) {
      setDay(value)
    }
  }, [value])

  // send value upstream
  useDidMountEffect(() => {
    if (day !== value) {
      if (onChange) {
        onChange(day)
      }
    }
  }, [day])

  // determine which hours and minutes should be disabled in the TimePicker
  // disabledHours and disabledMinutes are also used for validating hour and minute when day/time change
  function getDisabledHoursMinutes(day /* Date */) {
    let disabledBeforeHours = []
    let disabledBeforeMinutes = []
    let disabledAfterHours = []
    let disabledAfterMinutes = []
    if (disabledDays && day) {
      if (disabledDays.before) {
        // same days
        if (moment(disabledDays.before).isSame(day, "day")) {
          disabledBeforeHours = hours.filter((h) =>
            moment(disabledDays.before).isAfter(
              new Date(new Date(disabledDays.before).setHours(h))
            )
          )
          // same hours
          if (moment(disabledDays.before).isSame(day, "hour")) {
            disabledBeforeMinutes = minutes.filter((m) =>
              moment(disabledDays.before).isAfter(
                new Date(new Date(disabledDays.before).setMinutes(m))
              )
            )
          }
        }
      }

      if (disabledDays.after && day) {
        // same days
        if (moment(disabledDays.after).isSame(day, "day")) {
          disabledAfterHours = hours.filter((h) =>
            moment(disabledDays.after).isBefore(
              new Date(new Date(disabledDays.after).setHours(h))
            )
          )
          // same hours
          if (moment(disabledDays.after).isSame(day, "hour")) {
            disabledAfterMinutes = minutes.filter((m) =>
              moment(disabledDays.after).isBefore(
                new Date(new Date(disabledDays.after).setMinutes(m))
              )
            )
          }
        }
      }
    }

    const disabledHours = [...disabledBeforeHours, ...disabledAfterHours]
    const disabledMinutes = [...disabledBeforeMinutes, ...disabledAfterMinutes]

    return [disabledHours, disabledMinutes]
  }

  // gets non-disabled hour and min for date
  function getHourMin(day /* Date */) {
    let hour = moment(day).hour()
    let min = moment(day).minute()

    const [disabledHours, _] = getDisabledHoursMinutes(day)

    if (disabledHours.includes(hour)) {
      // try default hour
      if (!isNaN(defaultHour)) {
        hour = defaultHour
      }
      if (disabledHours.includes(hour)) {
        // the hour is disabled, find non-disabled hour
        hour = hours.find((h) => !disabledHours.includes(h))
      }
    }

    // if hour changed due to the previous value beind disabled, need to recalc disabledMinutes based on new hour value
    const [__, disabledMinutes] = getDisabledHoursMinutes(
      moment(day).hour(hour).toDate()
    )

    if (disabledMinutes.includes(min)) {
      // the minute is disabled, find non-disabled minute
      min = minutes.find((m) => !disabledMinutes.includes(m))
    }

    return [hour, min]
  }

  function handleDayChange(x /* Date */) {
    if (x) {
      let [hour, min] = getHourMin(
        moment(x)
          .hour(defaultHour || moment().hour())
          .minute(0)
          .toDate()
      )

      if (day) {
        // changing selected day but keeping hour and min from previous selection
        ;[hour, min] = getHourMin(
          moment(x).hour(day.getHours()).minute(day.getMinutes()).toDate()
        )
      }
      setDay(moment(x).hour(hour).minute(min).toDate()) // prettier-ignore
    } else {
      setDay(null)
    }
  }

  function handleTimeChange(x /* Date */) {
    const [hour, min] = getHourMin(moment(x).toDate())
    setDay(moment(x).hour(hour).minute(min).toDate()) // prettier-ignore
  }

  const [disabledHours, disabledMinutes] = getDisabledHoursMinutes(day)

  return (
    <div className="DayTimePicker">
      <div>
        <DayPicker
          {...rest}
          initialMonth={
            initialMonth || value || (disabledDays && disabledDays.before)
          }
          disabledDays={disabledDays}
          value={day}
          onChange={handleDayChange}
        />
      </div>
      {day ? (
        <TimePicker
          label={label}
          stretch={false}
          labelWidth={50}
          value={moment(day)}
          onChange={handleTimeChange}
          disabledHours={() => disabledHours}
          disabledMinutes={() => disabledMinutes}
          disabled={isDayDisabled(day, disabledDays)}
          custom={
            <Tooltip
              title={
                <React.Fragment>
                  <p style={{ whiteSpace: "normal", marginTop: "0" }}>
                    The time and date you set will display in each person’s
                    timezone (e.g. 9am ET displays as 8am in CT)
                  </p>
                  <a href="https://help.pathwright.com/en/articles/3991697-how-scheduling-works-across-multiple-time-zones">
                    {"More about timezones >"}
                  </a>
                </React.Fragment>
              }
            >
              <Pathicon
                icon="info-circle"
                style={{
                  display: "block",
                  color: "#8b9898",
                  marginLeft: "3px"
                }}
              />
            </Tooltip>
          }
        />
      ) : null}
    </div>
  )
}
